import type { AuthUser, OrgMembership } from "@/types";
import client from "./client";
import axios from "axios";

const API_BASE =
  import.meta.env.VITE_TRACKER_API_URL || "http://localhost:3020/api";

export interface AuthSessionResponse {
  token: string;
  user: AuthUser;
  organizations: OrgMembership[];
  currentOrg: { id: string; name: string; role: string } | null;
}

interface LoginResponse extends AuthSessionResponse {}

interface SignupResponse {
  token: string;
  user: AuthUser;
}

interface SelectOrgResponse {
  token: string;
  organization: {
    id: string;
    name: string;
    role: string;
  };
}

interface GetMeResponse {
  user: AuthUser;
  memberships: OrgMembership[];
  currentOrg: { id: string; name: string; role: string } | null;
}

const refreshClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  withCredentials: true,
  headers: {
    "X-Session-Client": "web",
  },
});

// ── Onboarding ────────────────────────────────────────────────────────────────

export interface OnboardPayload {
  fullName: string;
  orgName: string;
  email: string;
  teamSize: string;
  planId: string;
}

type OnboardResponse =
  | { status: "verify_email"; email: string }
  | { status: "checkout"; checkoutUrl: string };

export async function checkOrgNameApi(
  name: string,
): Promise<{ available: boolean }> {
  const { data } = await client.get<{ available: boolean }>(
    "/auth/check-org-name",
    { params: { name } },
  );
  return data;
}

export async function onboardApi(
  body: OnboardPayload,
): Promise<OnboardResponse> {
  const { data } = await client.post<OnboardResponse>("/auth/onboard", body);
  return data;
}

// ── Set password ──────────────────────────────────────────────────────────────

interface ValidateTokenResponse {
  valid: boolean;
  email?: string;
  fullName?: string | null;
  orgName?: string | null;
  reason?: string;
}

interface SetPasswordResponse {
  token: string;
  user: AuthUser;
  organization: (OrgMembership & { id: string; name: string }) | null;
}

export async function validateSetPasswordTokenApi(
  token: string,
): Promise<ValidateTokenResponse> {
  const { data } = await client.get<ValidateTokenResponse>(
    `/auth/set-password/${token}`,
  );
  return data;
}

export async function setPasswordApi(
  token: string,
  password: string,
): Promise<SetPasswordResponse> {
  const { data } = await client.post<SetPasswordResponse>(
    `/auth/set-password/${token}`,
    { password },
  );
  return data;
}

export async function forgotPasswordApi(email: string): Promise<void> {
  await client.post("/auth/forgot-password", { email });
}

// ── Auth ──────────────────────────────────────────────────────────────────────

export async function loginApi(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const { data } = await client.post<LoginResponse>("/auth/login", {
    email,
    password,
  });
  return data;
}

export async function refreshSessionApi(): Promise<AuthSessionResponse> {
  const { data } = await refreshClient.post<AuthSessionResponse>(
    "/auth/refresh",
    {},
  );
  return data;
}

export async function signupApi(
  email: string,
  password: string,
  fullName: string,
): Promise<SignupResponse> {
  const { data } = await client.post<SignupResponse>("/auth/signup", {
    email,
    password,
    fullName,
  });
  return data;
}

export async function selectOrgApi(orgId: string): Promise<SelectOrgResponse> {
  const { data } = await client.post<SelectOrgResponse>("/auth/select-org", {
    orgId,
  });
  return data;
}

export async function deselectOrgApi(): Promise<{ token: string }> {
  const { data } = await client.post<{ token: string }>("/auth/deselect-org");
  return data;
}

export async function getMeApi(): Promise<GetMeResponse> {
  const { data } = await client.get<GetMeResponse>("/auth/me");
  return data;
}

export async function logoutApi(): Promise<void> {
  await client.post("/auth/logout");
}

export async function updateProfileApi(body: {
  fullName?: string;
  jobTitle?: string;
  avatarUrl?: string;
}) {
  const { data } = await client.patch("/auth/profile", body);
  return data;
}

export async function changePasswordApi(
  currentPassword: string,
  newPassword: string,
) {
  const { data } = await client.post("/auth/change-password", {
    currentPassword,
    newPassword,
  });
  return data;
}
